import "$styles/medicion.css"

// Medición anónima hacia advantacloud (POST /help-center/events): búsquedas,
// búsquedas sin resultado, clics en resultados y "¿Te sirvió?".
//
// - Sólo si el build definió AYUDA_EVENTS_URL (llega como <meta name="ayuda-eventos">).
// - Nada si el navegador pide Do Not Track o Global Privacy Control.
// - Sin cookies: el único identificador es aleatorio, vive en sessionStorage
//   (una pestaña) y se renueva tras 30 min sin actividad.
// - JSON como text/plain: sendBeacon/fetch lo mandan sin preflight CORS.

const MAX_LOTE = 20
const ESPERA_ENVIO_MS = 5000
const QUIETUD_BUSQUEDA_MS = 1500
const SESION_TTL_MS = 30 * 60 * 1000

const meta = document.querySelector('meta[name="ayuda-eventos"]')
const destino = meta?.content
const privacidad = navigator.doNotTrack === "1" || window.doNotTrack === "1" || navigator.globalPrivacyControl === true
const activa = Boolean(destino) && !privacidad

const revision = /^[0-9a-f]{7,40}$/.test(meta?.dataset.revision || "") ? meta.dataset.revision : null
const cola = []
const antesDeSalir = []
let temporizador = null

const sesion = () => {
  try {
    const guardada = JSON.parse(sessionStorage.getItem("ayuda-medicion") || "null")
    const ahora = Date.now()
    const vigente = guardada && /^[0-9a-f]{32}$/.test(guardada.id) && ahora - guardada.t < SESION_TTL_MS
    const id = vigente
      ? guardada.id
      : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("")
    sessionStorage.setItem("ayuda-medicion", JSON.stringify({ id, t: ahora }))
    return id
  } catch {
    return null
  }
}

const enviar = () => {
  clearTimeout(temporizador)
  temporizador = null
  if (!cola.length) return
  const lote = { events: cola.splice(0, MAX_LOTE) }
  if (revision) lote.content_revision = revision
  const id = sesion()
  if (id) lote.session_hash = id
  const cuerpo = JSON.stringify(lote)

  let entregado = false
  try {
    entregado = navigator.sendBeacon?.(destino, cuerpo) === true
  } catch {
    entregado = false
  }
  if (!entregado) {
    fetch(destino, { method: "POST", body: cuerpo, keepalive: true, credentials: "omit", mode: "cors" }).catch(() => {})
  }
  if (cola.length) enviar()
}

const registrar = (evento) => {
  if (!activa) return
  cola.push({ ...evento, page_path: location.pathname })
  if (cola.length >= MAX_LOTE) enviar()
  else if (!temporizador) temporizador = setTimeout(enviar, ESPERA_ENVIO_MS)
}

if (activa) {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") enviar()
  })
  window.addEventListener("pagehide", () => {
    antesDeSalir.forEach((fn) => fn())
    enviar()
  })
}

// ---------- buscador (Pagefind UI) ----------
// Pagefind UI 1.x no emite eventos de resultados: se observa el contenedor.
// Mientras busca sólo pinta .pagefind-ui__message; con resultados pinta además
// ol.pagefind-ui__results (vacío si no hubo). El total sale del mensaje
// ("[COUNT] resultados encontrados…"); los <li> sólo son la primera página.
const normalizar = (texto) => texto.replace(/\s+/g, " ").trim().toLowerCase().slice(0, 200)

const medirBuscador = (contenedor) => {
  let ultima = null
  let espera = null

  const termino = () => normalizar(contenedor.querySelector(".pagefind-ui__search-input")?.value || "")

  const revisar = () => {
    clearTimeout(espera)
    espera = null
    const query = termino()
    const lista = contenedor.querySelector("ol.pagefind-ui__results")
    if (query.length < 2 || !lista) return
    const mensaje = contenedor.querySelector(".pagefind-ui__message")?.textContent || ""
    const total = lista.children.length ? Number(mensaje.match(/^\s*(\d+)/)?.[1] || lista.children.length) : 0
    const clave = `${query}\u0000${total}`
    if (clave === ultima) return
    ultima = clave
    registrar(total ? { kind: "search", query, results_count: total } : { kind: "zero_results", query, results_count: 0 })
  }

  const programar = () => {
    clearTimeout(espera)
    espera = setTimeout(revisar, QUIETUD_BUSQUEDA_MS)
  }

  new MutationObserver(programar).observe(contenedor, { childList: true, subtree: true, characterData: true })
  contenedor.addEventListener("input", programar)

  const clic = (event) => {
    const enlace = event.target.closest?.("a.pagefind-ui__result-link")
    if (!enlace || (event.type === "auxclick" && event.button !== 1)) return
    const resultado = enlace.closest(".pagefind-ui__result")
    const lista = resultado?.parentElement
    const query = termino()
    if (!lista || query.length < 2) return
    revisar()
    const url = new URL(enlace.href, location.href)
    if (url.origin !== location.origin) return
    registrar({
      kind: "result_click",
      query,
      rank: Array.prototype.indexOf.call(lista.children, resultado) + 1,
      target_path: url.pathname,
    })
    enviar()
  }
  contenedor.addEventListener("click", clic, true)
  contenedor.addEventListener("auxclick", clic, true)
}

// ---------- "¿Te sirvió?" ----------
const medirUtilidad = (seccion) => {
  const voto = seccion.querySelector(".sirvio-voto")
  const formulario = seccion.querySelector(".sirvio-motivo")
  const motivo = formulario.querySelector("input")
  const botonNo = seccion.querySelector('[data-sirvio-voto="no"]')
  const estado = seccion.querySelector(".sirvio-gracias")
  let pendiente = null

  const cerrar = () => {
    voto.hidden = true
    formulario.hidden = true
    estado.textContent = "Gracias."
  }

  // Un "no" espera el motivo; si la persona se va sin escribirlo, se manda sin él.
  antesDeSalir.push(() => {
    if (pendiente) registrar(pendiente)
    pendiente = null
  })

  seccion.addEventListener("click", (event) => {
    const boton = event.target.closest("[data-sirvio-voto]")
    if (!boton) return
    if (boton.dataset.sirvioVoto === "si") {
      pendiente = null
      registrar({ kind: "page_feedback", helpful: true })
      enviar()
      cerrar()
      return
    }
    pendiente = { kind: "page_feedback", helpful: false }
    botonNo.setAttribute("aria-expanded", "true")
    formulario.hidden = false
    motivo.focus()
  })

  formulario.addEventListener("submit", (event) => {
    event.preventDefault()
    if (!pendiente) return
    const texto = motivo.value.replace(/\s+/g, " ").trim().slice(0, 280)
    registrar(texto ? { ...pendiente, reason: texto } : pendiente)
    pendiente = null
    enviar()
    cerrar()
  })

  seccion.hidden = false
}

if (activa) {
  document.addEventListener("DOMContentLoaded", () => {
    const buscador = document.querySelector("#buscador")
    if (buscador) medirBuscador(buscador)
    document.querySelectorAll("[data-sirvio]").forEach(medirUtilidad)
  })
}
